import type React from 'react';
import raf from '@rc-component/util/lib/raf';
import { ConfigProvider } from 'antd-v6';

type ConfigProviderProps = React.ComponentProps<typeof ConfigProvider>;
type WaveConfig = NonNullable<ConfigProviderProps['wave']>;

// Ripple Wave Effect
const createHolder = (node: HTMLElement) => {
	const { borderWidth } = getComputedStyle(node);
	const borderWidthNum = Number.parseInt(borderWidth, 10);

	const div = document.createElement('div');
	div.style.position = 'absolute';
	div.style.inset = `-${borderWidthNum}px`;
	div.style.borderRadius = 'inherit';
	div.style.background = 'transparent';
	div.style.zIndex = '999';
	div.style.pointerEvents = 'none';
	div.style.overflow = 'hidden';
	node.appendChild(div);
	return div;
};

const createDot = (holder: HTMLElement, color: string, left: number, top: number, size = 0) => {
	const dot = document.createElement('div');
	dot.style.position = 'absolute';
	dot.style.insetInlineStart = `${left}px`;
	dot.style.top = `${top}px`;
	dot.style.width = `${size}px`;
	dot.style.height = `${size}px`;
	dot.style.borderRadius = '50%';
	dot.style.background = color;
	dot.style.transform = 'translate3d(-50%, -50%, 0)';
	dot.style.transition = 'all 1s ease-out';
	holder.appendChild(dot);
	return dot;
};

const showInsetEffect: WaveConfig['showEffect'] = (node, { event, component }) => {
	if (component !== 'Button') return;

	const holder = createHolder(node);
	const rect = holder.getBoundingClientRect();
	const left = event.clientX - rect.left;
	const top = event.clientY - rect.top;
	const dot = createDot(holder, 'rgba(255, 255, 255, 0.65)', left, top);

	raf(() => {
		dot.ontransitionend = () => holder.remove();
		dot.style.width = '200px';
		dot.style.height = '200px';
		dot.style.opacity = '0';
	});
};

export const muiWaveEffect: WaveConfig = { showEffect: showInsetEffect };

// Design Token overrides
export const getMuiToken = (darkMode: boolean, primaryColor: string) =>
	({
		colorPrimary: darkMode ? '#ffffff' : primaryColor,
		colorTextBase: darkMode ? '#ffffff' : '#1c1b1f',
		colorBgBase: darkMode ? '#121212' : '#fafafa',
		colorBgContainer: darkMode ? '#1e1e1e' : '#ffffff',
		colorBgElevated: darkMode ? '#2a2a2a' : '#ffffff',
		colorBgLayout: darkMode ? '#121212' : '#f5f5f5',
		colorBgSpotlight: darkMode ? 'rgba(255,255,255,0.85)' : 'rgba(33,33,33,0.85)',
		colorBgMask: darkMode ? 'rgba(0,0,0,0.5)' : 'rgba(33,33,33,0.5)',
		colorText: darkMode ? 'rgba(255,255,255,0.85)' : 'rgba(28,27,31,0.87)',
		colorTextSecondary: darkMode ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.6)',
		colorTextTertiary: darkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.38)',
		colorTextQuaternary: darkMode ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.26)',
		colorTextDisabled: darkMode ? 'rgba(255,255,255,0.30)' : 'rgba(0,0,0,0.38)',
		colorBorder: darkMode ? 'rgba(255,255,255,0.12)' : '#e0e0e0',
		colorBorderSecondary: darkMode ? 'rgba(255,255,255,0.08)' : '#eeeeee',
		borderRadius: 4,
		borderRadiusXS: 1,
		borderRadiusSM: 2,
		borderRadiusLG: 6,
		padding: 16,
		paddingSM: 8,
		paddingLG: 24,
		margin: 16,
		marginSM: 8,
		marginLG: 24,
		boxShadow: '0px 2px 1px -1px rgba(0,0,0,.2), 0px 1px 1px 0px rgba(0,0,0,.14), 0px 1px 3px 0px rgba(0,0,0,.12)',
		boxShadowSecondary:
			'0px 3px 3px -2px rgba(0,0,0,.2), 0px 3px 4px 0px rgba(0,0,0,.14), 0px 1px 8px 0px rgba(0,0,0,.12)',
	} as const);

// Component Token overrides
export const getMuiComponents = (darkMode: boolean, primaryColor: string) => ({
	Button: {
		primaryShadow:
			'0px 3px 1px -2px rgba(0,0,0,.2), 0px 2px 2px 0px rgba(0,0,0,.14), 0px 1px 5px 0px rgba(0,0,0,.12)',
		defaultShadow:
			'0px 3px 1px -2px rgba(0,0,0,.2), 0px 2px 2px 0px rgba(0,0,0,.14), 0px 1px 5px 0px rgba(0,0,0,.12)',
		dangerShadow:
			'0px 3px 1px -2px rgba(0,0,0,.2), 0px 2px 2px 0px rgba(0,0,0,.14), 0px 1px 5px 0px rgba(0,0,0,.12)',
		fontWeight: 500,
		defaultBorderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
		defaultColor: darkMode ? 'rgba(255,255,255,0.87)' : 'rgba(0,0,0,0.87)',
		defaultBg: darkMode ? '#1e1e1e' : '#ffffff',
		defaultHoverBg: darkMode ? 'rgba(255,255,255,0.04)' : `rgba(0,0,0,0.04)`,
		defaultHoverBorderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
		paddingInline: 16,
		paddingBlock: 6,
		contentFontSize: 14,
		borderRadius: 4,
	},
	Alert: { borderRadiusLG: 4 },
	Modal: { borderRadiusLG: 4 },
	Progress: {
		defaultColor: darkMode ? '#ffffff' : primaryColor,
		remainingColor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
	},
	Steps: { iconSize: 24 },
	Checkbox: { borderRadiusSM: 2 },
	Slider: {
		trackBg: darkMode ? 'rgba(255,255,255,0.26)' : 'rgba(0,0,0,0.26)',
		trackHoverBg: darkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.38)',
		handleSize: 20,
		handleSizeHover: 20,
		railSize: 4,
	},
	ColorPicker: { borderRadius: 4 },
	Input: {
		borderRadius: 4,
		controlHeight: 48,
		colorBgContainer: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
	},
	Select: {
		borderRadius: 4,
		colorBgContainer: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
	},
});
